import {
  BookOpen,
  Building2,
  Layers,
  ClipboardList,
  ShieldCheck,
  GitBranch,
  PackageOpen,
  PackageCheck,
  Factory,
  BookMarked,
  Boxes,
  ShoppingCart,
  Truck,
  FileText,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { Link } from 'react-router-dom';
import {
  Lead,
  P,
  H3,
  UL,
  LI,
  Code,
  Callout,
  Steps,
  Step,
  Flow,
  FlowNode,
  FlowArrow,
  Lifecycle,
  Figure,
  DefList,
  ChartCard,
} from './components';
import type { GuideChapter } from './tracks';

const ORANGE = 'hsl(22 100% 55%)';
const TEAL = 'hsl(188 75% 32%)';
const GRAY = 'hsl(220 9% 62%)';

// A styled internal link to a real ERP screen, so readers can jump straight in.
function GoTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[12px] font-medium text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15"
    >
      {children}
    </Link>
  );
}

// ── 1 · Overview ───────────────────────────────────────────────────────────
function Overview() {
  return (
    <div>
      <Lead>
        <strong className="text-foreground">Procurement</strong> is how raw materials enter your
        business — from raising a request, getting it approved, sourcing it (from a vendor or the open
        market), to physically receiving the goods and adding them to stock. Once materials are in
        stock, <strong className="text-foreground">Job Work</strong> turns them into finished goods.
        This guide walks the whole journey end to end.
      </Lead>

      <Callout tone="info" title="Who does what">
        A team member raises a <strong>Purchase Request (PR)</strong>; a manager/admin{' '}
        <strong>approves</strong> it; whoever sources the goods records the{' '}
        <strong>Goods Receipt (GRN)</strong> when they arrive. Every step is logged for audit.
      </Callout>

      <H3>The end-to-end flow</H3>
      <P>Everything below maps to a screen in the left nav under the “Procurement” section.</P>
      <Flow
        steps={[
          { icon: Building2, title: 'Vendors', sub: 'supplier master', tone: 'muted' },
          { icon: Layers, title: 'Raw materials', sub: 'item master', tone: 'muted' },
          { icon: ClipboardList, title: 'Purchase Request', sub: 'what you need', tone: 'accent' },
          { icon: ShieldCheck, title: 'Approval', sub: 'manager signs off' },
          { icon: GitBranch, title: 'Sourcing', sub: 'vendor / open market' },
          { icon: PackageOpen, title: 'Purchase Order', sub: 'issued to vendor' },
          { icon: PackageCheck, title: 'Goods Receipt', sub: 'GRN + docs', tone: 'success' },
          { icon: Boxes, title: 'Stock updated', sub: 'on hand ↑', tone: 'success' },
          { icon: Factory, title: 'Job Work', sub: '→ finished goods', tone: 'accent' },
        ]}
      />

      <H3>The five things you’ll touch</H3>
      <DefList
        items={[
          { term: 'Vendors', def: <>Your supplier master — GSTIN, PAN, addresses. Needed before vendor-sourced purchases.</> },
          { term: 'Raw Materials', def: <>The items you buy (cotton, zip, buttons…). Each carries an HSN code + GST rate.</> },
          { term: 'Purchase Requests', def: <>The heart of procurement: request → approve → source → receive.</> },
          { term: 'Purchase Orders', def: <>The formal order issued to a vendor, generated from an approved request.</> },
          { term: 'Goods Receipts (GRN)', def: <>Records what actually arrived, with the supplier’s invoice/challan/e-way bill.</> },
        ]}
      />

      <ChartCard title="Procurement funnel" hint="illustrative — your numbers appear in Reports">
        <FunnelChart />
      </ChartCard>
      <P>
        A healthy pipeline narrows smoothly from request to receipt. Big drop-offs at{' '}
        <em>Approval</em> can mean requests aren’t justified; stalls at <em>Received</em> mean goods
        are ordered but not arriving — worth chasing vendors.
      </P>
    </div>
  );
}

// ── 2 · Before you start ─────────────────────────────────────────────────────
function Setup() {
  return (
    <div>
      <Lead>
        Two masters should exist before you raise your first request. Five minutes of setup here saves
        re-typing later and keeps your GST correct downstream.
      </Lead>

      <Steps>
        <Step title="Add your vendors">
          Go to <GoTo to="/vendors">Vendors</GoTo> and add each supplier with their{' '}
          <strong>GSTIN, PAN and address</strong>. The GSTIN drives whether a purchase is intra-state
          (CGST+SGST) or inter-state (IGST), and the address prints on documents. You only need this
          for <em>vendor-sourced</em> buys — open-market cash purchases don’t require a vendor record.
        </Step>
        <Step title="Add your raw materials">
          Go to <GoTo to="/raw-materials">Raw Materials</GoTo> and create each item you buy. Give it a{' '}
          <strong>unit of measure</strong>, an <strong>8-digit HSN code</strong> and a{' '}
          <strong>cost price</strong>. The HSN auto-fills the GST rate from the platform master, so
          tax is right on every receipt and invoice.
        </Step>
        <Step title="Confirm your warehouse">
          Every request receives into a <strong>warehouse</strong>. Check yours exist under{' '}
          <GoTo to="/platform/warehouses">Warehouses</GoTo>. Stock lands in the warehouse the GRN
          records against.
        </Step>
      </Steps>

      <Callout tone="tip" title="Get HSN right once">
        Raw materials with a correct 8-digit HSN flow the right GST rate everywhere — receipts,
        job-work challans, and the finished-good invoice. Use the inventory “Sync HSN rates” tool to
        keep them aligned with the platform master.
      </Callout>
    </div>
  );
}

// ── 3 · Raising a request ────────────────────────────────────────────────────
function Request() {
  return (
    <div>
      <Lead>
        A <strong className="text-foreground">Purchase Request (PR)</strong> says “we need these
        materials.” It’s the single document that travels through approval, sourcing and receipt — so
        everything traces back to it.
      </Lead>

      <P>
        Open <GoTo to="/purchase-requests">Purchase Requests</GoTo> and click{' '}
        <strong>New request</strong>. A PR has a header and one or more lines:
      </P>

      <Figure label="Purchase Requests › New request" caption="A request groups everything you need into one approval.">
        <div className="space-y-3 text-[12.5px]">
          <div className="grid grid-cols-2 gap-3">
            <MockField label="Warehouse" value="Main Warehouse" />
            <MockField label="Sourcing type" value="Vendor" />
            <MockField label="Proposed vendor" value="New Shine Dyeing" />
            <MockField label="Needed by" value="18 Jun 2026" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border/60">
            <div className="grid grid-cols-[1fr_70px_90px] gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Item</span><span className="text-right">Qty</span><span className="text-right">Est. price</span>
            </div>
            <MockRow item="Cotton fabric 60s" qty="200 m" price="₹ 145" />
            <MockRow item="YKK zip 8 inch" qty="500 pc" price="₹ 12" />
          </div>
        </div>
      </Figure>

      <H3>Fields that matter</H3>
      <DefList
        items={[
          { term: 'Warehouse', def: 'Where the goods will be received. Stock lands here.' },
          { term: 'Sourcing type', def: <><strong>Vendor</strong> (you’ll raise a PO) or <strong>Open market</strong> (buy directly, no PO). You can change this at approval.</> },
          { term: 'Proposed vendor', def: 'Your suggested supplier — the approver can confirm or change it.' },
          { term: 'Needed by', def: 'Target date — helps approvers prioritise and surfaces late sourcing.' },
          { term: 'Lines', def: 'Each material + quantity + (optional) estimated unit price. The estimated total rolls up automatically.' },
          { term: 'Justification', def: 'Why you need it — speeds up approval, especially for large or unusual asks.' },
        ]}
      />

      <Callout tone="info" title="Draft vs submit">
        Save as a <strong>Draft</strong> to keep editing, or <strong>Submit</strong> to send it for
        approval. Submitting moves it to <Code>PENDING_APPROVAL</Code> and locks the lines.
      </Callout>
    </div>
  );
}

// ── 4 · Approval ─────────────────────────────────────────────────────────────
function Approval() {
  return (
    <div>
      <Lead>
        Approval is the control gate. A manager or admin reviews the request, can adjust the sourcing
        decision, and either approves or rejects with a note.
      </Lead>

      <P>
        On a submitted request, an approver sees <strong>Approve</strong> and <strong>Reject</strong>.
        At approval they can also set or change the <strong>sourcing type</strong> and{' '}
        <strong>vendor</strong> — so the person who raised it doesn’t have to know the final supplier.
      </P>

      <Lifecycle
        states={[
          { label: 'DRAFT', tone: 'muted' },
          { label: 'PENDING_APPROVAL', tone: 'warn' },
          { label: 'APPROVED', tone: 'accent' },
        ]}
        branches={[{ from: 'PENDING_APPROVAL', to: [{ label: 'REJECTED', tone: 'danger' }] }]}
      />

      <UL>
        <LI><strong>Approve</strong> → the request becomes <Code>APPROVED</Code> and is ready to source.</LI>
        <LI><strong>Reject</strong> → it becomes <Code>REJECTED</Code> with the reviewer’s note; the requester can revise and re-submit a new one.</LI>
      </UL>

      <Callout tone="tip" title="Separation of duties">
        Keep the requester and approver as different people for spend control. Roles are managed under{' '}
        <GoTo to="/platform/users">Members</GoTo> — approval is an admin/manager action.
      </Callout>
    </div>
  );
}

// ── 5 · Sourcing ─────────────────────────────────────────────────────────────
function Sourcing() {
  return (
    <div>
      <Lead>
        Once approved, the request is <strong className="text-foreground">sourced</strong>. There are
        two paths, chosen by the sourcing type — and they converge again at Goods Receipt.
      </Lead>

      <div className="my-5 space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <FlowNode icon={ClipboardList} title="Approved PR" tone="accent" />
          <FlowArrow />
          <FlowNode icon={PackageOpen} title="Vendor path" sub="raise a PO" />
          <FlowArrow />
          <FlowNode icon={FileText} title="Purchase Order" sub="sent to vendor" />
          <FlowArrow />
          <FlowNode icon={PackageCheck} title="Goods Receipt" tone="success" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <FlowNode icon={ClipboardList} title="Approved PR" tone="accent" />
          <FlowArrow />
          <FlowNode icon={ShoppingCart} title="Open-market path" sub="buy directly" />
          <FlowArrow />
          <FlowNode icon={PackageCheck} title="Goods Receipt" tone="success" />
        </div>
      </div>

      <H3>Vendor sourcing → Purchase Order</H3>
      <P>
        For <Code>VENDOR</Code> sourcing, click <strong>Convert to PO</strong> on the approved request.
        That generates a <strong>Purchase Order</strong> against the vendor with the request’s lines,
        which you can email/print and send. The request moves to <Code>SOURCED</Code>. (More on POs in
        the next chapter.)
      </P>

      <H3>Open-market sourcing</H3>
      <P>
        For <Code>OPEN_MARKET</Code> (cash/local purchases with no formal PO), skip the PO entirely —
        go straight to recording the <strong>Goods Receipt</strong> when the material arrives. This
        keeps small, ad-hoc buys fast while still capturing the bill and updating stock.
      </P>

      <ChartCard title="Where teams source from" hint="illustrative split">
        <SourcingSplit />
      </ChartCard>
    </div>
  );
}

// ── 6 · Purchase Orders ──────────────────────────────────────────────────────
function PurchaseOrder() {
  return (
    <div>
      <Lead>
        A <strong className="text-foreground">Purchase Order (PO)</strong> is the formal, numbered
        order you send a vendor. It’s your commitment record and what you receive against.
      </Lead>

      <P>
        POs live under <GoTo to="/purchase-orders">Purchase Orders</GoTo>. Most are created via{' '}
        <strong>Convert to PO</strong> from an approved request (carrying its lines and vendor), but
        you can also raise one directly. Set the <strong>expected delivery date</strong> and any
        notes, then send it to the vendor.
      </P>

      <Steps>
        <Step title="Issue the PO">
          Generated from the request or created directly. It gets a <Code>poNumber</Code> and lists
          each item, quantity and price.
        </Step>
        <Step title="Send to the vendor">
          Print or share the PO. The vendor fulfils against it.
        </Step>
        <Step title="Receive against it">
          When goods arrive, record a <strong>Goods Receipt</strong> linked to the PO — see the next
          chapter. Receipts can be partial; the PO tracks received vs ordered quantities.
        </Step>
      </Steps>

      <Callout tone="info" title="One PO, many receipts">
        Vendors often deliver in installments. You can record several GRNs against one PO; each adds to
        the received quantity until the order is fully received.
      </Callout>
    </div>
  );
}

// ── 7 · Goods Receipt ────────────────────────────────────────────────────────
function Grn() {
  return (
    <div>
      <Lead>
        The <strong className="text-foreground">Goods Receipt Note (GRN)</strong> is where materials
        actually enter your stock. It captures what arrived <em>and</em> the supplier’s paperwork — the
        compliance record for the purchase.
      </Lead>

      <P>
        From an approved/sourced request (or a PO), click <strong>Receive goods</strong>. Record the
        quantities that arrived and the supplier’s documents:
      </P>

      <Figure label="Purchase Requests › Receive goods" caption="The GRN links physical stock to the supplier’s invoice & e-way bill.">
        <div className="space-y-3 text-[12.5px]">
          <div className="grid grid-cols-2 gap-3">
            <MockField label="Supplier invoice no." value="NSD/26/0411" />
            <MockField label="Invoice date" value="14 Jun 2026" />
            <MockField label="Challan no." value="NSD-CH-882" />
            <MockField label="E-way bill no." value="3917 4421 0098" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border/60">
            <div className="grid grid-cols-[1fr_90px_90px] gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Item</span><span className="text-right">Ordered</span><span className="text-right">Received</span>
            </div>
            <MockRow item="Cotton fabric 60s" qty="200 m" price="200 m" />
            <MockRow item="YKK zip 8 inch" qty="500 pc" price="480 pc" />
          </div>
        </div>
      </Figure>

      <H3>What a GRN does</H3>
      <UL>
        <LI><strong>Increases stock on hand</strong> for each received item, in the request’s warehouse.</LI>
        <LI><strong>Records the supplier’s invoice, challan and e-way bill</strong> — attach the PDF/photo for audit.</LI>
        <LI><strong>Tracks received vs ordered</strong> per line, so partial deliveries are obvious.</LI>
        <LI>Moves the request to <Code>RECEIVED</Code> (then <Code>CLOSED</Code> when fully received).</LI>
      </UL>

      <Lifecycle
        states={[
          { label: 'APPROVED', tone: 'accent' },
          { label: 'SOURCED', tone: 'default' },
          { label: 'RECEIVED', tone: 'success' },
          { label: 'CLOSED', tone: 'muted' },
        ]}
      />

      <Callout tone="warn" title="Receive what actually arrived">
        Enter the <em>real</em> received quantity, not the ordered one. Under-deliveries (480 of 500
        zips above) keep the line open so you can record the balance later — and your stock stays
        truthful.
      </Callout>
    </div>
  );
}

// ── 8 · Into production ──────────────────────────────────────────────────────
function IntoProduction() {
  return (
    <div>
      <Lead>
        Procurement ends when materials are in stock — but that’s the start of making product. Here’s
        how the raw materials you just received become finished goods.
      </Lead>

      <Flow
        steps={[
          { icon: Boxes, title: 'Raw materials', sub: 'in stock', tone: 'success' },
          { icon: BookOpen, title: 'Product Recipe', sub: 'BOM + route', tone: 'default' },
          { icon: Factory, title: 'Job Work Order', sub: 'consume → produce', tone: 'accent' },
          { icon: Truck, title: 'Issue challan', sub: 'if outsourced' },
          { icon: PackageCheck, title: 'Finished good', sub: 'ready to sell', tone: 'success' },
        ]}
      />

      <UL>
        <LI>
          A <GoTo to="/job-work/recipes">Product Recipe</GoTo> defines how a finished good is made —
          its bill of materials and step-by-step route.
        </LI>
        <LI>
          A <GoTo to="/job-work/orders">Job Work Order</GoTo> runs a step: it <strong>consumes</strong>{' '}
          the raw materials (stock out) and <strong>produces</strong> the output (stock in), in-house
          or outsourced to a contractor.
        </LI>
        <LI>
          For outsourced steps, print the <strong>General Issue Challan</strong> (GST Rule-55) for the
          material going out, and the <strong>return receipt</strong> when processed goods come back.
        </LI>
      </UL>

      <Callout tone="info" title="A separate guide is coming">
        End-to-end <strong>sales of finished goods</strong> — orders, invoicing, dispatch and delivery
        — will get its own track in this guide. For now, Job Work is the bridge from materials to
        sellable product.
      </Callout>
    </div>
  );
}

// ── 9 · Reference ────────────────────────────────────────────────────────────
function Reference() {
  return (
    <div>
      <Lead>A quick reference for the request lifecycle, plus the mistakes that trip teams up.</Lead>

      <H3>Status glossary</H3>
      <DefList
        items={[
          { term: <Code>DRAFT</Code>, def: 'Being written; editable, not yet sent for approval.' },
          { term: <Code>PENDING_APPROVAL</Code>, def: 'Submitted and waiting for a manager/admin decision.' },
          { term: <Code>APPROVED</Code>, def: 'Cleared to source. Convert to PO (vendor) or receive directly (open market).' },
          { term: <Code>REJECTED</Code>, def: 'Declined with a reviewer note. Raise a fresh request if still needed.' },
          { term: <Code>SOURCED</Code>, def: 'A PO has been raised against the request.' },
          { term: <Code>RECEIVED</Code>, def: 'Goods received (fully or partially) via a GRN; stock updated.' },
          { term: <Code>CLOSED</Code>, def: 'Fully received and complete — no further action.' },
          { term: <Code>CANCELLED</Code>, def: 'Abandoned before completion.' },
        ]}
      />

      <H3>Common pitfalls</H3>
      <UL>
        <LI><strong>No HSN on a raw material</strong> → GST shows blank on receipts and downstream challans. Set the 8-digit HSN.</LI>
        <LI><strong>Receiving the ordered qty instead of the delivered qty</strong> → stock overstates reality. Enter what physically arrived.</LI>
        <LI><strong>Missing supplier invoice on the GRN</strong> → weak audit trail. Always attach the bill / e-way bill.</LI>
        <LI><strong>Open-market buy with a vendor PO</strong> → unnecessary paperwork. Use the open-market path for cash/local buys.</LI>
      </UL>

      <Callout tone="success" title="You’ve got the full loop">
        Vendors &amp; materials → request → approval → sourcing → PO → GRN → stock → job work. That’s
        procurement, end to end. Jump into <GoTo to="/purchase-requests">Purchase Requests</GoTo> to
        raise your first one.
      </Callout>
    </div>
  );
}

// ── Mock UI helpers (for Figures) ────────────────────────────────────────────
function MockField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium text-foreground">{value}</div>
    </div>
  );
}
function MockRow({ item, qty, price }: { item: string; qty: string; price: string }) {
  return (
    <div className="grid grid-cols-[1fr_90px_90px] gap-2 border-b border-border/40 px-3 py-2 last:border-0">
      <span className="text-foreground">{item}</span>
      <span className="text-right tabular-nums text-muted-foreground">{qty}</span>
      <span className="text-right tabular-nums text-muted-foreground">{price}</span>
    </div>
  );
}

// ── Charts ───────────────────────────────────────────────────────────────────
function FunnelChart() {
  const data = [
    { stage: 'Requested', value: 100, fill: GRAY },
    { stage: 'Approved', value: 82, fill: TEAL },
    { stage: 'Sourced', value: 76, fill: TEAL },
    { stage: 'Received', value: 71, fill: ORANGE },
  ];
  return (
    <div className="h-[180px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 28, left: 8, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="stage"
            width={78}
            tickLine={false}
            axisLine={false}
            fontSize={12}
            stroke="hsl(220 9% 50%)"
          />
          <Tooltip
            cursor={{ fill: 'hsl(220 13% 91% / 0.4)' }}
            contentStyle={{ borderRadius: 8, border: '1px solid hsl(220 13% 91%)', fontSize: 12 }}
            formatter={(v: number) => [`${v} requests`, '']}
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.fill} />
            ))}
            <LabelList dataKey="value" position="right" fontSize={11} fill="hsl(220 9% 46%)" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
function SourcingSplit() {
  const data = [
    { name: 'Vendor (with PO)', value: 68, fill: ORANGE },
    { name: 'Open market', value: 32, fill: TEAL },
  ];
  return (
    <div className="h-[180px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={42} outerRadius={66} paddingAngle={2}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.fill} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ borderRadius: 8, border: '1px solid hsl(220 13% 91%)', fontSize: 12 }}
            formatter={(v: number, n: string) => [`${v}%`, n]}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Chapters export ──────────────────────────────────────────────────────────
export const procurementChapters: GuideChapter[] = [
  { id: 'overview', title: 'Overview', icon: BookOpen, render: Overview },
  { id: 'setup', title: 'Before you start', icon: Building2, render: Setup },
  { id: 'request', title: 'Raising a request', icon: ClipboardList, render: Request },
  { id: 'approval', title: 'Approval', icon: ShieldCheck, render: Approval },
  { id: 'sourcing', title: 'Sourcing', icon: GitBranch, render: Sourcing },
  { id: 'purchase-order', title: 'Purchase Orders', icon: PackageOpen, render: PurchaseOrder },
  { id: 'grn', title: 'Goods Receipt (GRN)', icon: PackageCheck, render: Grn },
  { id: 'production', title: 'Into production', icon: Factory, render: IntoProduction },
  { id: 'reference', title: 'Reference & pitfalls', icon: BookMarked, render: Reference },
];
